import type { ComponentType } from "react";
import { cn } from "@nexus/ui";
import { useTabIndicator } from "./useTabIndicator";

export interface DrawerTabDef<T extends string> {
  id: T;
  label: string;
  Icon: ComponentType<{ className?: string }>;
  count?: number;
}

interface DrawerTabStripProps<T extends string> {
  tabs: Array<DrawerTabDef<T>>;
  activeTab: T;
  onTabChange: (tab: T) => void;
}

/** Animated tab strip with a sliding underline indicator, shared by the Org, Person, and Role drawers. */
export function DrawerTabStrip<T extends string>({ tabs, activeTab, onTabChange }: DrawerTabStripProps<T>) {
  const { indicator, registerTabRef } = useTabIndicator(tabs.findIndex((t) => t.id === activeTab));

  return (
    <div className="relative flex border-b border-border px-4">
      {tabs.map((t, i) => (
        <button
          key={t.id}
          ref={registerTabRef(i)}
          onClick={() => onTabChange(t.id)}
          className={cn(
            "inline-flex items-center gap-1.5 px-2.5 py-2.5 text-[12px] font-medium transition-colors",
            activeTab === t.id ? "text-foreground" : "text-muted-foreground hover:text-foreground",
          )}
        >
          <t.Icon className="h-3 w-3" />
          {t.label}
          {t.count !== undefined && (
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-muted px-1 font-mono text-[10px] text-muted-foreground">
              {t.count}
            </span>
          )}
        </button>
      ))}
      <div
        className="absolute bottom-0 h-0.5 bg-primary"
        style={{
          left: indicator.left,
          width: indicator.width,
          transition: "left 200ms cubic-bezier(0.4,0,0.2,1), width 200ms cubic-bezier(0.4,0,0.2,1)",
        }}
      />
    </div>
  );
}
